import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { locatairesRepository } from "@/repositories/locatairesRepository";
import type {
  LocataireInsert,
  LocataireRow,
  LocataireUpdate,
} from "@/schemas/locataire";

export const locatairesKeys = {
  all: ["locataires"] as const,
  list: (bienId: string) => ["locataires", "list", bienId] as const,
};

/** Liste tous les locataires (actuel + anciens) d'un bien. */
export function useLocataires(bienId: string | undefined) {
  return useQuery<LocataireRow[]>({
    queryKey: bienId ? locatairesKeys.list(bienId) : ["locataires", "list", "__none__"],
    queryFn: () => (bienId ? locatairesRepository.list(bienId) : Promise.resolve([])),
    enabled: !!bienId,
  });
}

export function useCreateLocataire(bienId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: LocataireInsert) => locatairesRepository.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: locatairesKeys.list(bienId) }),
  });
}

export function useUpdateLocataire(bienId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: LocataireUpdate }) =>
      locatairesRepository.update(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: locatairesKeys.list(bienId) }),
  });
}

export function useDeleteLocataire(bienId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => locatairesRepository.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: locatairesKeys.list(bienId) }),
  });
}

/**
 * Transfère le locataire actuel → ancien, crée un nouveau slot actuel vide.
 * Une seule transaction côté DB (RPC `transfer_locataire`, migration 127) —
 * voir le commentaire de `locatairesRepository.transfer`.
 */
export function useTransferLocataire(bienId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ currentId, archivedDateFin }: {
      currentId: string | null;
      archivedDateFin?: string;
    }) => locatairesRepository.transfer(bienId, currentId, archivedDateFin),
    onSuccess: () => qc.invalidateQueries({ queryKey: locatairesKeys.list(bienId) }),
  });
}
